import { getAuth } from 'firebase/auth';
import type { TokenBundle, SubscriptionOption } from '@/lib/bundles';

const FUNCTIONS_BASE = import.meta.env.VITE_FUNCTIONS_BASE as string;

export interface FetchBillingOptionsResult {
  bundles: TokenBundle[];
  subscription: SubscriptionOption | null;
}

/**
 * Fetch token bundles and subscription option from the Cloud Function. Used by the Top up screen.
 */
export async function fetchBillingOptions(): Promise<FetchBillingOptionsResult> {
  const res = await fetch(`${FUNCTIONS_BASE}/getTokenBundles`, { method: 'GET' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  const bundles = Array.isArray(data.bundles)
    ? data.bundles.filter(
        (b: unknown): b is TokenBundle =>
          typeof b === 'object' &&
          b !== null &&
          typeof (b as TokenBundle).id === 'string' &&
          typeof (b as TokenBundle).tokens === 'number' &&
          typeof (b as TokenBundle).amountCents === 'number' &&
          typeof (b as TokenBundle).label === 'string'
      )
    : [];
  const sub = data.subscription;
  const subscription: SubscriptionOption | null =
    sub &&
    typeof sub.tokensPerMonth === 'number' &&
    typeof sub.label === 'string' &&
    typeof sub.amountCents === 'number'
      ? { tokensPerMonth: sub.tokensPerMonth, label: sub.label, amountCents: sub.amountCents }
      : null;
  return { bundles, subscription };
}

/**
 * Create a Stripe Checkout session for the given bundle and return the redirect URL.
 * Requires the user to be signed in.
 */
export async function createCheckoutSession(bundleId: string): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required');
  const token = await user.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ bundleId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  if (!data.url || typeof data.url !== 'string') throw new Error('No checkout URL returned');
  return data.url;
}

/**
 * Create a Stripe Checkout session for the monthly subscription. Returns redirect URL.
 */
export async function createSubscriptionCheckoutSession(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required');
  const token = await user.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}/createSubscriptionCheckoutSession`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  if (!data.url || typeof data.url !== 'string') throw new Error('No checkout URL returned');
  return data.url;
}

export interface TokenTransaction {
  id: string;
  type: 'usage' | 'topup_otp' | 'topup_subscription' | 'topup_share';
  amount: number;
  balanceAfter: number;
  createdAt: number;
  metadata?: Record<string, string>;
}

/**
 * Fetch token transaction history for the current user. Auth required.
 */
export async function fetchTokenTransactions(limit = 50): Promise<TokenTransaction[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required');
  const token = await user.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}/getTokenTransactions?limit=${limit}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  if (!Array.isArray(data.transactions)) return [];
  return data.transactions.filter(
    (t: unknown): t is TokenTransaction =>
      typeof t === 'object' &&
      t !== null &&
      typeof (t as TokenTransaction).id === 'string' &&
      ['usage', 'topup_otp', 'topup_subscription', 'topup_share'].includes((t as TokenTransaction).type) &&
      typeof (t as TokenTransaction).amount === 'number' &&
      typeof (t as TokenTransaction).balanceAfter === 'number' &&
      (typeof (t as TokenTransaction).createdAt === 'number' || typeof (t as TokenTransaction).createdAt === 'string')
  );
}

/**
 * Whether the current user has an active subscription. Auth required.
 */
export async function fetchSubscriptionStatus(): Promise<boolean> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required');
  const token = await user.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}/getSubscriptionStatus`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return !!data.hasActiveSubscription;
}

/**
 * Get a Stripe Customer Portal URL to manage subscription. Redirect user to this URL.
 */
export async function createBillingPortalSession(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in required');
  const token = await user.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE}/createBillingPortalSession`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  if (!data.url || typeof data.url !== 'string') throw new Error('No portal URL returned');
  return data.url;
}
